<template>
  <div class="beneficiary-page">
    <!-- 로딩 컴포넌트 -->
    <LodingComponent :is-visible="isLoading" text="데이터 로딩 중..." />
    
    <div class="beneficiary-layout" :class="{ 'sidebar-right': sidebarPosition === 'right' }" v-if="!isLoading">
      <!-- 고정 사이드바 (리모컨) -->
      <aside class="beneficiary-sidebar">
        <!-- 위치 토글 버튼 -->
        <button 
          class="sidebar-position-toggle"
          @click="toggleSidebarPosition"
          :title="sidebarPosition === 'left' ? '우측으로 이동' : '좌측으로 이동'"
        >
          <v-icon size="14">{{ sidebarPosition === 'left' ? 'mdi-arrow-right' : 'mdi-arrow-left' }}</v-icon>
        </button>
        
        <!-- 페이지 타이틀 배지 -->
        <div class="sidebar-title-badge">
          <v-icon size="16">mdi-account-heart</v-icon>
          <span>수급자 관리</span>
        </div>
        
        <!-- 탭 선택 -->
        <div class="sidebar-tabs">
          <button 
            class="sidebar-tab" 
            :class="{ active: activeTab === 'manage' }"
            @click="activeTab = 'manage'; fetchBeneficiaryInfoList()"
          >
            <v-icon size="16">mdi-account-cog</v-icon>
            <span>수급자 관리</span>
          </button>
          <button 
            class="sidebar-tab" 
            :class="{ active: activeTab === 'schedule' }"
            @click="activeTab = 'schedule'"
          >
            <v-icon size="16">mdi-calendar-account</v-icon>
            <span>일정 목록</span>
          </button>
        </div>

        <!-- 통계 요약 -->
        <div class="sidebar-summary">
          <div class="summary-item beneficiary-count">
            <div class="summary-header">
              <span class="dot"></span>
              <span class="label">전체 수급자</span>
            </div>
            <span class="value">{{ beneficiaryList.length }}명</span>
          </div>
          <div class="summary-divider"></div>
          <div class="summary-item active-count">
            <div class="summary-header">
              <span class="dot"></span>
              <span class="label">이번달 수급자</span>
            </div>
            <span class="value">{{ activeThisMonth }}명</span>
          </div>
          <div class="summary-divider"></div>
          <div class="summary-item caregiver-count">
            <div class="summary-header">
              <span class="dot"></span>
              <span class="label">총 서비스 개월</span>
            </div>
            <span class="value">{{ totalServiceMonths }}개월</span>
          </div>
        </div>

        <!-- 검색 필터 -->
        <div class="sidebar-search">
          <div class="search-header">
            <v-icon size="14" color="#64748b">mdi-magnify</v-icon>
            <span>수급자 검색</span>
          </div>
          <div class="search-input-wrapper">
            <input
              type="text"
              class="search-input"
              v-model="searchKeyword"
              placeholder="이름으로 검색..."
            />
            <button v-if="searchKeyword" class="search-clear" @click="searchKeyword = ''">
              <v-icon size="12">mdi-close</v-icon>
            </button>
          </div>
        </div>

        <!-- 기간 선택 -->
        <div class="sidebar-period">
          
          <!-- 전체보기 버튼 -->
          <button 
            class="period-all-btn" 
            :class="{ active: periodMode === 'all' }"
            @click="setPeriodMode('all')"
          >
            <v-icon size="20">mdi-calendar-multiple</v-icon>
            <span>전체 기간 보기</span>
          </button>

          <!-- 월별 선택 영역 -->
          <div class="period-month-section">
            <div class="period-month-label">또는 특정 월 선택:</div>
            <div class="period-month-nav">
              <button class="month-nav-btn prev" @click="prevMonth" :disabled="!canGoPrev">
                <v-icon size="24">mdi-chevron-left</v-icon>
                <span>이전</span>
              </button>
              <button 
                class="period-current-month" 
                :class="{ active: periodMode === 'month' }"
                @click="setPeriodMode('month')"
              >
                <div class="current-year">{{ selectedYear }}년</div>
                <div class="current-month">{{ selectedMonth }}월</div>
              </button>
              <button class="month-nav-btn next" @click="nextMonth" :disabled="!canGoNext">
                <span>다음</span>
                <v-icon size="24">mdi-chevron-right</v-icon>
              </button>
            </div>
          </div>
        </div>

      </aside>

      <!-- 메인 컨텐츠 영역 -->
      <main class="beneficiary-main">
        <!-- 수급자 일정 목록 탭 -->
        <section v-if="activeTab === 'schedule'" class="beneficiary-list-section">
          <div class="section-header">
            <div class="header-title">
              <div class="title-icon">
                <v-icon size="18" color="white">mdi-calendar-account</v-icon>
              </div>
              <span class="title-text">수급자 일정 목록</span>
              <span class="title-count">{{ filteredBeneficiaryList.length }}명</span>
              <span class="title-badge">일정계획 기준</span>
            </div>
            <div class="header-actions">
              <button 
                class="batch-pdf-btn"
                @click="printAllPatientsPdf"
                :disabled="isPdfLoading || filteredBeneficiaryList.length === 0 || periodMode === 'all'"
                :title="periodMode === 'all' ? '특정 월을 선택해주세요' : '전체 수급자 일정표 PDF 출력'"
              >
                <v-icon size="18">{{ isPdfLoading ? 'mdi-loading mdi-spin' : 'mdi-file-pdf-box' }}</v-icon>
                <span>전체 일정표 출력</span>
              </button>
              <span class="current-month-label">{{ displayPeriodLabel }} 기준</span>
            </div>
          </div>

          <table class="beneficiary-table" v-if="filteredBeneficiaryList.length > 0">
            <thead>
              <tr>
                <th class="col-no">No</th>
                <th>수급자명</th>
                <th>수급자코드</th>
                <th class="text-center">첫 서비스</th>
                <th class="text-center">최근 서비스</th>
                <th class="text-center">총 서비스</th>
                <th class="text-center col-action">상세보기</th>
                <th class="text-center col-action">일정표</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(beneficiary, index) in filteredBeneficiaryList" :key="beneficiary.patient_code">
                <!-- 수급자 행 -->
                <tr 
                  :class="{ selected: selectedBeneficiary?.patient_code === beneficiary.patient_code }"
                  class="beneficiary-row"
                >
                  <td class="col-no">{{ index + 1 }}</td>
                  <td>
                    <div class="patient-name">
                      <div class="patient-icon">
                        <v-icon size="12">mdi-account</v-icon>
                      </div>
                      {{ beneficiary.patient_name }}
                    </div>
                  </td>
                  <td>{{ beneficiary.patient_code }}</td>
                  <td class="text-center">
                    <span class="date-text">{{ formatDateShort(beneficiary.first_service_date) }}</span>
                  </td>
                  <td class="text-center">
                    <span class="date-text">{{ formatDateShort(beneficiary.last_service_date) }}</span>
                  </td>
                  <td class="text-center">
                    <span class="service-months">{{ beneficiary.total_months }}개월</span>
                  </td>
                  <td class="text-center col-action">
                    <button 
                      class="action-btn detail-btn"
                      :class="{ active: selectedBeneficiary?.patient_code === beneficiary.patient_code }"
                      @click="toggleBeneficiary(beneficiary)"
                      title="상세보기"
                    >
                      <v-icon size="16">
                        {{ selectedBeneficiary?.patient_code === beneficiary.patient_code ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                      </v-icon>
                    </button>
                  </td>
                  <td class="text-center col-action">
                    <button 
                      class="action-btn schedule-btn"
                      @click="printSchedulePdf(beneficiary)"
                      title="일정표 PDF 출력"
                    >
                      <v-icon size="16">mdi-file-pdf-box</v-icon>
                    </button>
                  </td>
                </tr>
                <!-- 상세 정보 확장 행 -->
                <tr 
                  v-if="selectedBeneficiary?.patient_code === beneficiary.patient_code" 
                  class="detail-row"
                >
                  <td colspan="8">
                    <div class="inline-detail">
                      <div class="monthly-summary" v-if="monthlySummary.length > 0">
                        <div 
                          class="month-card" 
                          v-for="month in monthlySummary" 
                          :key="month.year_month"
                        >
                          <div class="month-header">
                            <span class="month-label">{{ formatYearMonth(month.year_month) }}</span>
                          </div>
                          <div class="month-stats">
                            <div class="stat-item">
                              <div class="stat-value">{{ month.service_days }}</div>
                              <div class="stat-label">서비스일</div>
                            </div>
                            <div class="stat-item">
                              <div class="stat-value">{{ formatMinutes(month.total_minutes) }}</div>
                              <div class="stat-label">총 시간</div>
                            </div>
                          </div>
                          <div class="month-caregivers" v-if="month.caregivers">
                            <v-icon size="12" class="mr-1">mdi-account-heart</v-icon>
                            <span>{{ month.caregivers }}</span>
                          </div>
                          <div class="month-sugas" v-if="month.suga_types">
                            <v-icon size="12" class="mr-1">mdi-clipboard-list</v-icon>
                            <span>{{ month.suga_types }}</span>
                          </div>
                        </div>
                      </div>
                      <div class="empty-text" v-else>서비스 이력 데이터가 없습니다</div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>

          <div class="empty-state" v-else>
            <div class="empty-icon">
              <v-icon size="36">mdi-calendar-account-outline</v-icon>
            </div>
            <div class="empty-text">일정계획 데이터가 없습니다</div>
            <div class="empty-hint">일정계획 파일이 업로드되면 수급자 일정이 표시됩니다</div>
          </div>
        </section>

        <!-- 수급자 관리 탭 -->
        <section v-if="activeTab === 'manage'" class="beneficiary-manage-section">
          <div class="section-header">
            <div class="header-title">
              <div class="title-icon manage">
                <v-icon size="18" color="white">mdi-account-cog</v-icon>
              </div>
              <span class="title-text">수급자 정보 관리</span>
              <span class="title-count">{{ beneficiaryInfoList.length }}명</span>
            </div>
            <div class="header-actions">
              <button 
                class="sync-btn"
                @click="syncBeneficiaries"
                :disabled="isSyncing"
                title="일정계획에서 수급자 동기화"
              >
                <v-icon size="18">{{ isSyncing ? 'mdi-loading mdi-spin' : 'mdi-sync' }}</v-icon>
                <span>일정계획에서 동기화</span>
              </button>
            </div>
          </div>

          <div class="table-wrapper" v-if="filteredBeneficiaryInfoList.length > 0">
            <table class="manage-table">
              <thead>
                <tr>
                  <th class="col-no">No</th>
                  <th>수급자명</th>
                  <th>장기요양인정번호</th>
                  <th class="text-center">생년월일</th>
                  <th class="text-center">등급</th>
                  <th class="text-center">본인부담율</th>
                  <th class="text-center col-action">수정</th>
                </tr>
              </thead>
              <tbody>
                <tr 
                  v-for="(info, index) in filteredBeneficiaryInfoList" 
                  :key="info.bf_patient_code"
                  class="beneficiary-row"
                  :class="{ editing: editingPatientCode === info.bf_patient_code }"
                >
                  <td class="col-no">{{ index + 1 }}</td>
                  <td>
                    <div class="patient-name">
                      <div class="patient-icon">
                        <v-icon size="12">mdi-account</v-icon>
                      </div>
                      {{ info.bf_patient_name }}
                    </div>
                  </td>
                  <td>{{ info.bf_patient_code }}</td>
                  <td class="text-center">
                    <span v-if="editingPatientCode !== info.bf_patient_code" class="date-text">
                      {{ info.bf_birth || '-' }}
                    </span>
                    <input 
                      v-else 
                      type="text" 
                      class="edit-input"
                      :value="editForm.birth"
                      @input="onBirthInput"
                      placeholder="YYYY-MM-DD"
                      maxlength="10"
                    />
                  </td>
                  <td class="text-center">
                    <span v-if="editingPatientCode !== info.bf_patient_code">
                      <span v-if="info.bf_grade" class="grade-badge">{{ info.bf_grade }}</span>
                      <span v-else class="empty-value">-</span>
                    </span>
                    <input 
                      v-else 
                      type="text" 
                      class="edit-input"
                      v-model="editForm.grade"
                      placeholder="등급 입력"
                      maxlength="20"
                    />
                  </td>
                  <td class="text-center">
                    <span v-if="editingPatientCode !== info.bf_patient_code">
                      <span v-if="info.bf_copay_rate" class="copay-badge">{{ info.bf_copay_rate }}</span>
                      <span v-else class="empty-value">-</span>
                    </span>
                    <input 
                      v-else 
                      type="text" 
                      class="edit-input"
                      v-model="editForm.copayRate"
                      placeholder="본인부담율 입력"
                      maxlength="20"
                    />
                  </td>
                  <td class="text-center col-action">
                    <template v-if="editingPatientCode !== info.bf_patient_code">
                      <button 
                        class="action-btn edit-btn"
                        @click="startEdit(info)"
                        title="수정"
                      >
                        <v-icon size="16">mdi-pencil</v-icon>
                      </button>
                    </template>
                    <template v-else>
                      <button 
                        class="action-btn save-btn"
                        @click="saveEdit()"
                        :disabled="isSaving"
                        title="저장"
                      >
                        <v-icon size="16">{{ isSaving ? 'mdi-loading mdi-spin' : 'mdi-check' }}</v-icon>
                      </button>
                      <button 
                        class="action-btn cancel-btn"
                        @click="cancelEdit()"
                        title="취소"
                      >
                        <v-icon size="16">mdi-close</v-icon>
                      </button>
                    </template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="empty-state" v-else>
            <div class="empty-icon">
              <v-icon size="36">mdi-account-plus-outline</v-icon>
            </div>
            <div class="empty-text">등록된 수급자 정보가 없습니다</div>
            <div class="empty-hint">"일정계획에서 동기화" 버튼을 클릭하여 수급자를 등록하세요</div>
          </div>
        </section>
      </main>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';
import LodingComponent from '@/components/loading/LodingComponent.vue';

// Types
interface Beneficiary {
  patient_code: string;
  patient_name: string;
  first_service_date: string;
  last_service_date: string;
  total_months: number;
}

interface MonthlySummary {
  year_month: string;
  service_days: number;
  caregiver_count: number;
  caregivers: string;
  total_minutes: number;
  suga_types: string;
}

interface BeneficiaryInfo {
  bf_idx: number;
  m_sisul_code: string;
  bf_patient_code: string;
  bf_patient_name: string;
  bf_birth: string | null;
  bf_grade: string | null;
  bf_copay_rate: string | null;
  bf_copay_type: string | null;
  bf_phone: string | null;
  bf_address: string | null;
  bf_start_date: string | null;
  bf_end_date: string | null;
  bf_memo: string | null;
  bf_status: 'active' | 'inactive';
}

interface EditForm {
  patientCode: string;
  patientName: string;
  birth: string;
  grade: string;
  copayRate: string;
  copayType: string;
  phone: string;
  address: string;
  startDate: string;
  endDate: string;
  memo: string;
  status: string;
}

// Store
const store = useStore();
const sisulCode = computed(() => store.getters.institutionCode);

// State
const isLoading = ref(true);
const isPdfLoading = ref(false);
const isSyncing = ref(false);
const isSaving = ref(false);
const sidebarPosition = ref<'left' | 'right'>('right');
const searchKeyword = ref('');
const activeTab = ref<'schedule' | 'manage'>('manage');

const beneficiaryList = ref<Beneficiary[]>([]);
const periodBeneficiaries = ref<Set<string>>(new Set());
const selectedBeneficiary = ref<Beneficiary | null>(null);

const monthlySummary = ref<MonthlySummary[]>([]);

// 수급자 관리 탭 관련 상태
const beneficiaryInfoList = ref<BeneficiaryInfo[]>([]);
const editingPatientCode = ref<string | null>(null);
const editForm = ref<EditForm>({
  patientCode: '',
  patientName: '',
  birth: '',
  grade: '',
  copayRate: '',
  copayType: '',
  phone: '',
  address: '',
  startDate: '',
  endDate: '',
  memo: '',
  status: 'active'
});

// 현재 년월 (YYYYMM)
const now = new Date();
const currentYear = now.getFullYear();
const currentMonth = now.getMonth() + 1;

// 기간 선택 관련 상태
const periodMode = ref<'all' | 'month'>('month');
const selectedYear = ref(currentYear);
const selectedMonth = ref(currentMonth);

// 선택 가능한 연도 (현재 연도 기준 5년 전부터 현재까지)
const availableYears = computed(() => {
  const years: number[] = [];
  for (let y = currentYear - 5; y <= currentYear; y++) {
    years.push(y);
  }
  return years;
});

// 선택 가능한 월 (1~12월)
const availableMonths = computed(() => {
  return Array.from({ length: 12 }, (_, i) => i + 1);
});

// 다음달 계산
const nextMonthYear = currentMonth === 12 ? currentYear + 1 : currentYear;
const nextMonthMonth = currentMonth === 12 ? 1 : currentMonth + 1;

// 이전/다음 월 이동 가능 여부
const canGoPrev = computed(() => {
  return !(selectedYear.value === currentYear - 5 && selectedMonth.value === 1);
});

const canGoNext = computed(() => {
  // 다음달까지 선택 가능
  return !(selectedYear.value === nextMonthYear && selectedMonth.value === nextMonthMonth);
});

// 선택된 기간 (YYYYMM 형식)
const selectedYM = computed(() => {
  return `${selectedYear.value}${String(selectedMonth.value).padStart(2, '0')}`;
});

// 화면에 표시할 기간 라벨
const displayPeriodLabel = computed(() => {
  if (periodMode.value === 'all') {
    return '전체 기간';
  }
  return `${selectedYear.value}년 ${selectedMonth.value}월`;
});

// Computed
const filteredBeneficiaryList = computed(() => {
  let list = beneficiaryList.value;
  
  // 검색 필터
  if (searchKeyword.value) {
    list = list.filter(b => b.patient_name.includes(searchKeyword.value));
  }
  
  // 기간 필터 (월별 모드일 때만 해당 기간 수급자만 표시)
  if (periodMode.value === 'month') {
    list = list.filter(b => periodBeneficiaries.value.has(b.patient_code));
  }
  
  return list;
});

const activeThisMonth = computed(() => {
  return periodBeneficiaries.value.size;
});

const totalServiceMonths = computed(() => {
  return beneficiaryList.value.reduce((sum, b) => sum + (b.total_months || 0), 0);
});

// 수급자 관리 탭 - 검색 필터 적용
const filteredBeneficiaryInfoList = computed(() => {
  let list = beneficiaryInfoList.value;
  
  if (searchKeyword.value) {
    list = list.filter(b => b.bf_patient_name.includes(searchKeyword.value));
  }
  
  return list;
});

// Methods
const toggleSidebarPosition = () => {
  sidebarPosition.value = sidebarPosition.value === 'left' ? 'right' : 'left';
};

// 기간 모드 설정
const setPeriodMode = (mode: 'all' | 'month') => {
  periodMode.value = mode;
  fetchPeriodBeneficiaries();
};

// 이전 월로 이동
const prevMonth = () => {
  if (!canGoPrev.value) return;
  
  if (selectedMonth.value === 1) {
    selectedYear.value--;
    selectedMonth.value = 12;
  } else {
    selectedMonth.value--;
  }
};

// 다음 월로 이동
const nextMonth = () => {
  if (!canGoNext.value) return;
  
  if (selectedMonth.value === 12) {
    selectedYear.value++;
    selectedMonth.value = 1;
  } else {
    selectedMonth.value++;
  }
};

const formatMinutes = (minutes: number): string => {
  if (!minutes) return '0분';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins}분`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
};

const formatYearMonth = (ym: string): string => {
  if (!ym || ym.length !== 6) return ym;
  return `${ym.substring(0, 4)}년 ${parseInt(ym.substring(4))}월`;
};

const formatDateShort = (dateStr: string): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
};

const isActiveThisMonth = (beneficiary: Beneficiary): boolean => {
  return periodBeneficiaries.value.has(beneficiary.patient_code);
};

// 전체 수급자 목록 조회
const fetchBeneficiaryList = async () => {
  if (!sisulCode.value) return;
  
  try {
    const response = await axios.get(
      `/api/beneficiary/list/${sisulCode.value}`
    );
    
    if (response.data.success) {
      beneficiaryList.value = response.data.data;
    }
  } catch (error) {
    console.error('수급자 목록 조회 오류:', error);
  }
};

// 선택 기간 수급자 목록 조회
const fetchPeriodBeneficiaries = async () => {
  if (!sisulCode.value) return;
  
  try {
    let url = '';
    
    if (periodMode.value === 'all') {
      // 전체 기간: 모든 수급자가 해당
      periodBeneficiaries.value = new Set(beneficiaryList.value.map(b => b.patient_code));
      return;
    } else {
      // 특정 월: 해당 월의 수급자만 조회
      url = `/api/beneficiary/list/${sisulCode.value}/${selectedYM.value}`;
    }
    
    const response = await axios.get(url);
    
    if (response.data.success) {
      const codes = new Set<string>();
      response.data.data.forEach((b: { patient_code: string }) => {
        codes.add(b.patient_code);
      });
      periodBeneficiaries.value = codes;
    }
  } catch (error) {
    console.error('기간별 수급자 조회 오류:', error);
  }
};

// 수급자 토글 (클릭 시 확장/축소)
const toggleBeneficiary = async (beneficiary: Beneficiary) => {
  // 이미 선택된 수급자를 다시 클릭하면 닫기
  if (selectedBeneficiary.value?.patient_code === beneficiary.patient_code) {
    selectedBeneficiary.value = null;
    return;
  }
  
  selectedBeneficiary.value = beneficiary;
  await fetchMonthlySummary(beneficiary.patient_code);
};

const fetchMonthlySummary = async (patientCode: string) => {
  if (!sisulCode.value) return;
  
  try {
    const response = await axios.get(
      `/api/beneficiary/monthly/${sisulCode.value}/${patientCode}`
    );
    
    if (response.data.success) {
      monthlySummary.value = response.data.data;
    }
  } catch (error) {
    console.error('월별 요약 조회 오류:', error);
    monthlySummary.value = [];
  }
};

// 일정표 PDF 출력
const printSchedulePdf = async (beneficiary: Beneficiary) => {
  if (!sisulCode.value) return;
  
  // 전체 기간 모드인 경우 특정 월 선택 요청
  if (periodMode.value === 'all') {
    alert('일정표 PDF 출력을 위해 특정 월을 선택해주세요.\n\n좌측 사이드바에서 원하는 년/월을 선택한 후 다시 시도해주세요.');
    return;
  }
  
  try {
    const yearMonth = selectedYM.value;
    
    const response = await axios.get(
      `/api/beneficiary/schedule-pdf/${sisulCode.value}/${beneficiary.patient_code}/${yearMonth}`,
      { responseType: 'blob' }
    );
    
    // PDF 다운로드
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `일정표_${beneficiary.patient_name}_${yearMonth}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('일정표 PDF 출력 오류:', error);
    alert('일정표 PDF 출력에 실패했습니다.');
  }
};

// 전체 수급자 일정표 PDF 일괄 출력
const printAllPatientsPdf = async () => {
  if (!sisulCode.value) return;
  
  // 전체 기간 모드인 경우 특정 월 선택 요청
  if (periodMode.value === 'all') {
    alert('일정표 PDF 출력을 위해 특정 월을 선택해주세요.\n\n사이드바에서 원하는 년/월을 선택한 후 다시 시도해주세요.');
    return;
  }
  
  const patientCount = filteredBeneficiaryList.value.length;
  if (patientCount === 0) {
    alert('출력할 수급자가 없습니다.');
    return;
  }
  
  // 확인 메시지
  if (!confirm(`${selectedYear.value}년 ${selectedMonth.value}월 전체 수급자(${patientCount}명)의 일정표를 PDF로 출력하시겠습니까?\n\n데이터 양에 따라 시간이 소요될 수 있습니다.`)) {
    return;
  }
  
  try {
    isPdfLoading.value = true;
    const yearMonth = selectedYM.value;
    
    const response = await axios.get(
      `/api/beneficiary/all-patients-schedule-pdf/${sisulCode.value}/${yearMonth}`,
      { responseType: 'blob' }
    );
    
    // PDF 다운로드
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `전체수급자_일정표_${yearMonth}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('전체 수급자 일정표 PDF 출력 오류:', error);
    alert('전체 수급자 일정표 PDF 출력에 실패했습니다.');
  } finally {
    isPdfLoading.value = false;
  }
};

// 수급자 정보 목록 조회
const fetchBeneficiaryInfoList = async () => {
  if (!sisulCode.value) return;
  
  try {
    const response = await axios.get(
      `/api/beneficiary/info/list/${sisulCode.value}`
    );
    
    if (response.data.success) {
      beneficiaryInfoList.value = response.data.data;
    }
  } catch (error) {
    console.error('수급자 정보 목록 조회 오류:', error);
  }
};

// 일정계획에서 수급자 동기화
const syncBeneficiaries = async () => {
  if (!sisulCode.value) return;
  
  if (!confirm('일정계획에 있는 수급자를 수급자 정보 테이블에 동기화하시겠습니까?')) {
    return;
  }
  
  try {
    isSyncing.value = true;
    
    const response = await axios.post(
      `/api/beneficiary/info/sync/${sisulCode.value}`
    );
    
    if (response.data.success) {
      alert(response.data.message);
      await fetchBeneficiaryInfoList();
    } else {
      alert(response.data.message || '동기화에 실패했습니다.');
    }
  } catch (error) {
    console.error('수급자 동기화 오류:', error);
    alert('수급자 동기화에 실패했습니다.');
  } finally {
    isSyncing.value = false;
  }
};

// 수정 시작
const startEdit = (info: BeneficiaryInfo) => {
  editingPatientCode.value = info.bf_patient_code;
  editForm.value = {
    patientCode: info.bf_patient_code,
    patientName: info.bf_patient_name,
    birth: info.bf_birth || '',
    grade: info.bf_grade || '',
    copayRate: info.bf_copay_rate || '',
    copayType: info.bf_copay_type || '',
    phone: info.bf_phone || '',
    address: info.bf_address || '',
    startDate: info.bf_start_date || '',
    endDate: info.bf_end_date || '',
    memo: info.bf_memo || '',
    status: info.bf_status || 'active'
  };
};

// 수정 취소
const cancelEdit = () => {
  editingPatientCode.value = null;
};

// 생년월일 입력 시 자동 하이픈 포맷팅
const onBirthInput = (event: Event) => {
  const input = event.target as HTMLInputElement;
  let value = input.value.replace(/[^0-9]/g, ''); // 숫자만 추출
  
  // 최대 8자리까지만 허용 (YYYYMMDD)
  if (value.length > 8) {
    value = value.substring(0, 8);
  }
  
  // 하이픈 자동 삽입
  let formatted = '';
  if (value.length > 0) {
    formatted = value.substring(0, 4); // YYYY
    if (value.length > 4) {
      formatted += '-' + value.substring(4, 6); // MM
      if (value.length > 6) {
        formatted += '-' + value.substring(6, 8); // DD
      }
    }
  }
  
  editForm.value.birth = formatted;
  input.value = formatted;
};

// 수정 저장
const saveEdit = async () => {
  if (!sisulCode.value || !editForm.value.patientCode) return;
  
  try {
    isSaving.value = true;
    
    const response = await axios.post(
      `/api/beneficiary/info/${sisulCode.value}`,
      editForm.value
    );
    
    if (response.data.success) {
      alert(response.data.message);
      editingPatientCode.value = null;
      await fetchBeneficiaryInfoList();
    } else {
      alert(response.data.message || '저장에 실패했습니다.');
    }
  } catch (error) {
    console.error('수급자 정보 저장 오류:', error);
    alert('수급자 정보 저장에 실패했습니다.');
  } finally {
    isSaving.value = false;
  }
};

const initializeData = async () => {
  isLoading.value = true;
  
  try {
    await fetchBeneficiaryList();
    await fetchPeriodBeneficiaries();
    // 기본 탭이 수급자 관리이므로 수급자 정보도 로드
    await fetchBeneficiaryInfoList();
  } catch (error) {
    console.error('데이터 초기화 오류:', error);
  } finally {
    isLoading.value = false;
  }
};

// Watchers
watch(sisulCode, () => {
  if (sisulCode.value) {
    initializeData();
  }
});

// 연도나 월이 변경되면 해당 기간 수급자 다시 조회
watch([selectedYear, selectedMonth], () => {
  if (periodMode.value === 'month') {
    fetchPeriodBeneficiaries();
  }
});

// Lifecycle
onMounted(() => {
  if (sisulCode.value) {
    initializeData();
  } else {
    isLoading.value = false;
  }
});
</script>

<style lang="scss" scoped>
@import '@/assets/styles/pgmBeneficiary.scss';
</style>
